import { createPortal } from "react-dom";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckVATIDPricingPage,
  VATRulesPricingPage
} from "@eclear-ag/ec-common-ui/pricing";
import { ECAxiosAPI } from "@eclear-ag/ec-common-ui";
import {
  getSubscriptionDetailPath,
  getUpgradeWizardPath
} from "./subscription-product-config";

const PRICING_PAGES = {
  CHECKVATID: CheckVATIDPricingPage,
  VATRULES: VATRulesPricingPage
};

/** True when this session's browser history has a real entry to go back to
 *  (react-router stamps history.state.idx on every entry it creates; a
 *  fresh/copy-pasted URL load has no prior entry, i.e. idx is 0 or unset). */
function hasNavigableHistory() {
  return (window.history.state?.idx ?? 0) > 0;
}

/** Absolute URL for an in-app path. Stripe rejects relative URLs, so the
 *  success/cancel targets handed to the shared pricing page must be full. */
function absoluteUrl(path) {
  return typeof window !== "undefined"
    ? `${window.location.origin}${path}`
    : path;
}

export default function UpgradeWizardRoute() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const product = searchParams.get("product");
  const PricingPage = product ? PRICING_PAGES[product] : undefined;

  if (!PricingPage) {
    return <Navigate to="/spot/manage-profile?tab=subscriptions" replace />;
  }

  const detailPath = getSubscriptionDetailPath(product);
  // Stripe returns the browser to the wizard's OWN url. The shared pricing page
  // appends ?productId= to it, and on arrival detects that param and shows its
  // polling card straight away — so the user sees "setting up your account"
  // rather than the plan grid while polling runs behind it. Returning to the
  // detail page instead would leave the subscription polling nowhere.
  const wizardUrl = absoluteUrl(getUpgradeWizardPath(product));

  const goBackToDetail = () => {
    if (hasNavigableHistory()) {
      navigate(-1);
      return;
    }
    navigate(detailPath, { replace: true });
  };

  // Rendered full screen via a portal to document.body, escaping the app
  // shell (header/sidebar/tab panel) entirely — the URL stays the same, only
  // what's on screen changes. A portal alone only re-parents into document.body
  // as a normal-flow sibling *after* the app root, so without an explicit
  // fixed/full-viewport wrapper here it renders correctly but pushed below the
  // fold, appearing blank. This wrapper is what actually makes it "full screen."
  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        overflowY: "auto",
        zIndex: 1400
      }}
    >
      <PricingPage
        axios={ECAxiosAPI}
        navigate={(url) => {
          if (url === detailPath) {
            goBackToDetail();
            return;
          }
          window.location.href = url;
        }}
        // Where the wizard closes to once the subscription is done (the
        // "Go to dashboard" action) — distinct from the Stripe return targets.
        successPath={detailPath}
        // The in-app "back out of the wizard" target, routed through the
        // injected navigate above. NOT a Stripe URL — that is stripeCancelUrl
        // below, which is where Stripe itself returns the browser to.
        cancelPath={detailPath}
        // Stripe return targets, supplied by this app because the shared page
        // is mounted by several apps whose destinations differ. Cancel comes
        // back to the wizard so the user can pick another plan instead of being
        // dropped out of it.
        stripeSuccessUrl={wizardUrl}
        stripeCancelUrl={wizardUrl}
      />
    </div>,
    document.body
  );
}
